<script lang="ts">
	// The design system's Button (design/components/core/Button.jsx) in
	// Svelte. The tones, sizes and states are the design's own and every value
	// is one of its tokens; press, focus and busy come from interaction.css
	// through the data-press and data-ground attributes, exactly as there.
	//
	// Two things are the app's, not the design's:
	//
	// testId is required: the end-to-end tests select by test id only.
	//
	// Press feedback also starts on pointerdown (pressable.ts), so a finger
	// gets the same 1px press within 50 ms rather than when :active arrives.
	//
	// Disabled is not busy, as the design says: disabled means this cannot
	// happen now (Pay with an empty cart); pending means it is happening, and
	// keeps its tone. The disabled look is the design's own, in
	// interaction.css; this only puts the right attribute on the element.
	// Text tones have no ground to sink, so they are marked "bare" and struck.
	//
	// A pending button says what it is doing in words, never with a spinner:
	// the compass is for panels (Loading), and at button size it is a smudge.
	//
	// An icon sits before the word, in its colour, 20 in a small button and 22
	// otherwise, as the design draws it. The design composes the Icon in as a
	// child; here it is named with `icon`, which draws the same glyph.

	import Icon, { type IconName } from './Icon.svelte';
	import { pressable } from './pressable';

	type Tone =
		'primary' | 'ink' | 'outline' | 'outline-on-ink' | 'secondary' | 'text' | 'text-quiet';

	interface Props {
		testId: string;
		label: string;
		/** A glyph before the label. The label is still required: an icon sits beside a word. */
		icon?: IconName;
		/** A glyph after the label, as the design's picker ("Downtown ▾") and its way out to another site do. */
		trailingIcon?: IconName;
		/** Sends the form it sits in, as the design's type="submit" does. */
		submit?: boolean;
		onPress?: () => void;
		tone?: Tone;
		size?: 'lg' | 'md' | 'sm';
		state?: 'idle' | 'pending' | 'done';
		pendingLabel?: string;
		doneLabel?: string;
		/** The white G tile on the Google sign-in button. */
		google?: boolean;
		/** Renders a link instead of a button. */
		href?: string;
		/** A link that opens in a new tab, as the design's target="_blank" does. */
		newTab?: boolean;
		disabled?: boolean;
		/** Fills the width it is given. Layout, not a style. */
		wide?: boolean;
		/**
		 * A label too long for its room is cut at its end with an ellipsis, as
		 * the till header cuts the signed-in name.
		 */
		truncate?: boolean;
		/**
		 * The name read aloud when the label is empty. Only the design's
		 * universal controls (plus, minus, close, back) stand alone this way.
		 */
		ariaLabel?: string;
		/**
		 * A tooltip naming what the button does, as the design passes title on
		 * a receipt section's icon-only toggles (D11 Printing).
		 */
		title?: string;
		/** Mono figures, for a button whose label is an amount. */
		mono?: boolean;
		/**
		 * A count before the label, in mono figures that read left to right,
		 * as the Till page draws the open orders on the header ("3 open").
		 */
		figure?: number;
		/** Mirrors a directional icon, such as back on an English page. */
		mirrorIcon?: boolean;
		/** One of a set of buttons that picks one, as the report's periods do: says which is on. */
		pressed?: boolean;
		/** A toggle that shows or hides something below it, as the till's order Details does: says whether it is open. */
		expanded?: boolean;
		/** The id of what a toggle shows or hides, for assistive technology. */
		controls?: string;
		/**
		 * A glyph alone in a square touch target, as the design draws Up and
		 * Down beside a grip. Needs `icon`, an empty label and `ariaLabel`.
		 */
		square?: boolean;
		/**
		 * A drag handle on a reorderable row (the design's "drag to reorder"
		 * pattern): marks the button data-grip, which interaction.css styles,
		 * and stops a touch on it from scrolling, so the finger drags instead.
		 * The row's owner does the moving from these two events.
		 */
		grip?: {
			onPointerDown: (event: PointerEvent) => void;
			onKeyDown: (event: KeyboardEvent) => void;
		};
	}

	let {
		testId,
		label,
		icon,
		onPress,
		tone = 'primary',
		size = 'lg',
		// Named phase here: a local called state shadows the $state rune.
		state: phase = 'idle',
		pendingLabel,
		doneLabel,
		google = false,
		href,
		newTab = false,
		disabled = false,
		wide = false,
		truncate = false,
		ariaLabel,
		title,
		mono = false,
		figure,
		mirrorIcon = false,
		pressed,
		expanded,
		controls,
		square = false,
		grip,
		trailingIcon,
		submit = false,
	}: Props = $props();

	// Which ground the focus ring reads against, as in the design: a red ring
	// on a red fill is invisible.
	const grounds: Partial<Record<Tone, string>> = {
		primary: 'speed',
		ink: 'invert',
		'outline-on-ink': 'invert',
	};

	const busy = $derived(phase === 'pending');
	const done = $derived(phase === 'done');
	const off = $derived(disabled && !busy);
	const inert = $derived(busy || off);
	const bare = $derived(tone === 'text' || tone === 'text-quiet');
	const press = $derived(tone === 'primary' ? 'lift' : bare ? 'bare' : '');
	const shown = $derived(busy ? (pendingLabel ?? label) : done ? (doneLabel ?? label) : label);

	function activate(event: MouseEvent) {
		if (inert) {
			event.preventDefault();
			return;
		}
		onPress?.();
	}
</script>

{#snippet content()}
	{#if google}
		<span class="g" aria-hidden="true">G</span>
	{/if}
	{#if done}
		<!-- The design's `check`, drawn heavier for the done state, as there. -->
		<svg
			class="tick"
			viewBox="0 0 24 24"
			width="18"
			height="18"
			fill="none"
			stroke="currentColor"
			stroke-width="2.2"
			stroke-linecap="round"
			stroke-linejoin="round"
			aria-hidden="true"
		>
			<path d="M5 12.5l4.5 4.5L19 7.5" />
		</svg>
	{/if}
	<!-- The icon is part of the label in the design, so a pending or done
	     label that replaces the words replaces it too. -->
	{#if icon && !(busy && pendingLabel) && !(done && doneLabel)}
		<Icon name={icon} size={size === 'sm' ? 20 : 22} mirror={mirrorIcon} />
	{/if}
	{#if figure !== undefined}
		<bdi class="figure" dir="ltr">{figure}</bdi>
	{/if}
	{#if truncate}<span class="label">{shown}</span>{:else}{shown}{/if}
	{#if trailingIcon && !(busy && pendingLabel) && !(done && doneLabel)}
		<Icon name={trailingIcon} size={18} />
	{/if}
{/snippet}

{#if href}
	<a
		href={off ? undefined : href}
		target={newTab ? '_blank' : undefined}
		rel={newTab ? 'noopener' : undefined}
		class="btn {tone} {size}"
		class:truncate
		class:wide
		class:mono
		aria-label={ariaLabel}
		data-testid={testId}
		data-press={press}
		data-ground={grounds[tone]}
		aria-busy={busy || undefined}
		aria-disabled={inert || undefined}
		aria-live={busy || done ? 'polite' : undefined}
		onclick={activate}
		{@attach pressable}
	>
		{@render content()}
	</a>
{:else}
	<button
		type={submit ? 'submit' : 'button'}
		class="btn {tone} {size}"
		class:truncate
		class:wide
		class:mono
		class:square={square || !!grip}
		class:grip={!!grip}
		data-grip={grip ? '' : undefined}
		aria-pressed={pressed}
		aria-expanded={expanded}
		aria-controls={controls}
		onpointerdown={grip?.onPointerDown}
		onkeydown={grip?.onKeyDown}
		aria-label={ariaLabel}
		{title}
		data-testid={testId}
		data-press={press}
		data-ground={grounds[tone]}
		disabled={inert}
		aria-busy={busy || undefined}
		aria-disabled={inert || undefined}
		aria-live={busy || done ? 'polite' : undefined}
		onclick={activate}
		{@attach pressable}
	>
		{@render content()}
	</button>
{/if}

<style>
	/* design/components/core/Button.jsx: base, then sizes, then tones, in that
	 * order, so a tone's padding wins over a size's as it does there. */
	.btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 10px;
		white-space: nowrap;
		font-family: var(--font-sans);
		font-weight: 600;
		text-decoration: none;
		border-radius: var(--radius-btn);
		border: none;
		cursor: pointer;
		min-block-size: var(--ctl-min);
	}

	.lg {
		font-size: 16.5px;
		padding: 15px 22px;
	}

	.md {
		font-size: 16px;
		padding: 14px 20px;
	}

	.sm {
		font-size: 14px;
		padding: 9px 15px;
		gap: 8px;
		border-radius: var(--radius-input);
	}

	.primary {
		background: var(--speed);
		color: var(--on-speed);
		box-shadow: var(--shadow-press);
	}

	.ink {
		background: var(--surface-invert);
		color: var(--on-ink);
	}

	.outline {
		background: transparent;
		color: var(--text-strong);
		border: var(--border-width-strong) solid var(--text-strong);
	}

	.outline-on-ink {
		background: transparent;
		color: var(--on-ink);
		border: var(--border-width-strong) solid var(--rule-on-ink);
	}

	.secondary {
		background: var(--surface-card);
		color: var(--text-strong);
		border: var(--border-width) solid var(--border-default);
	}

	.text {
		background: transparent;
		color: var(--accent-speed);
		padding: 8px 4px;
		box-shadow: none;
	}

	.text-quiet {
		background: transparent;
		color: var(--text-muted);
		padding: 8px 4px;
		box-shadow: none;
	}

	.truncate {
		min-inline-size: 0;
		max-inline-size: 100%;
	}

	.label {
		min-inline-size: 0;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.wide {
		inline-size: 100%;
	}

	.mono {
		font-family: var(--font-mono);
	}

	.figure {
		font-family: var(--font-mono);
		font-weight: 700;
	}

	/* A glyph alone in a square touch target. */
	.square {
		inline-size: var(--tap-min);
		min-inline-size: var(--tap-min);
		min-block-size: var(--tap-min);
		padding: 0;
	}

	/* A drag handle: no scrolling from a touch that starts on it. */
	.grip {
		touch-action: none;
	}

	/* The G tile on the Google sign-in button. */
	.g {
		inline-size: 22px;
		block-size: 22px;
		background: var(--on-speed);
		color: var(--speed);
		border-radius: 4px;
		display: grid;
		place-items: center;
		font-family: var(--font-mono);
		font-weight: 600;
		font-size: 14px;
		flex: none;
	}

	.tick {
		display: block;
		flex: none;
	}
</style>
